import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Copy, Code, FormInput, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadCapturePage() {
  const [forms, setForms] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', business_id: '', list_id: '', fields: ['name', 'email', 'phone'], success_message: 'Thank you for subscribing!' });
  const [selectedForm, setSelectedForm] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [fRes, bRes, lRes] = await Promise.all([
        axios.get(`${API}/lead-forms`, { withCredentials: true }),
        axios.get(`${API}/businesses`, { withCredentials: true }),
        axios.get(`${API}/lists`, { withCredentials: true }),
      ]);
      setForms(fRes.data);
      setBusinesses(bRes.data);
      setLists(lRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/lead-forms`, form, { withCredentials: true });
      toast.success('Lead form created');
      setOpen(false);
      setForm({ name: '', business_id: '', list_id: '', fields: ['name', 'email', 'phone'], success_message: 'Thank you for subscribing!' });
      fetchAll();
      setSelectedForm(res.data);
    } catch (e) { toast.error('Failed to create form'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this form?')) return;
    try {
      await axios.delete(`${API}/lead-forms/${id}`, { withCredentials: true });
      toast.success('Form deleted');
      if (selectedForm?.form_id === id) setSelectedForm(null);
      fetchAll();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const generateEmbedCode = (f) => {
    return `<!-- LCBSE Lead Capture Form -->
<form id="lcbse-form-${f.form_id}" onsubmit="return lcbseSubmit(event, '${f.form_id}')">
  ${f.fields?.includes('name') ? '<input type="text" name="name" placeholder="Your Name" required style="display:block;width:100%;padding:10px;margin:8px 0;border:1px solid #ccc;border-radius:4px;" />' : ''}
  ${f.fields?.includes('email') ? '<input type="email" name="email" placeholder="Your Email" required style="display:block;width:100%;padding:10px;margin:8px 0;border:1px solid #ccc;border-radius:4px;" />' : ''}
  ${f.fields?.includes('phone') ? '<input type="tel" name="phone" placeholder="Phone Number" style="display:block;width:100%;padding:10px;margin:8px 0;border:1px solid #ccc;border-radius:4px;" />' : ''}
  <button type="submit" style="display:block;width:100%;padding:12px;background:#00FF99;color:#000;border:none;border-radius:4px;font-weight:bold;cursor:pointer;">Subscribe</button>
</form>
<script>
function lcbseSubmit(e, formId) {
  e.preventDefault();
  var form = e.target;
  var data = {};
  new FormData(form).forEach(function(v, k) { data[k] = v; });
  fetch('${BACKEND_URL}/api/leads/capture/' + formId, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }).then(function(r) { return r.json(); })
    .then(function(d) { alert(d.message || 'Thank you!'); form.reset(); })
    .catch(function() { alert('Something went wrong.'); });
  return false;
}
</script>`;
  };

  const generateApiDoc = (f) => {
    return `POST ${BACKEND_URL}/api/leads/capture/${f.form_id}
Content-Type: application/json

{
  "name": "John Doe",
  "email": "john@example.com",
  "phone": "+1234567890",
  "service": "Optional service info"
}

Response:
{
  "status": "success",
  "message": "${f.success_message || 'Thank you!'}"
}`;
  };

  const getBizName = (id) => businesses.find(b => b.business_id === id)?.name || '—';

  return (
    <div className="flex-1 p-8" data-testid="lead-capture-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>Lead Capture</h1>
          <p className="text-[#B0B0B0] text-sm mt-1">Create forms and API endpoints for your websites</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-lead-form-btn" className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] rounded-md">
              <Plus size={16} className="mr-2" /> New Form
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>New Lead Capture Form</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Form Name</Label>
                <Input data-testid="lead-form-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Homepage Signup" />
              </div>
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Business</Label>
                <Select value={form.business_id} onValueChange={v => setForm(p => ({ ...p, business_id: v }))}>
                  <SelectTrigger className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                    <SelectValue placeholder="Select business" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                    {businesses.map(b => (
                      <SelectItem key={b.business_id} value={b.business_id} className="text-white hover:bg-[#1A1A1A]">{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Auto-add to List</Label>
                <Select value={form.list_id} onValueChange={v => setForm(p => ({ ...p, list_id: v }))}>
                  <SelectTrigger className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                    <SelectValue placeholder="Select list (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                    {lists.map(l => (
                      <SelectItem key={l.list_id} value={l.list_id} className="text-white hover:bg-[#1A1A1A]">{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Success Message</Label>
                <Input value={form.success_message} onChange={e => setForm(p => ({ ...p, success_message: e.target.value }))}
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Thank you for subscribing!" />
              </div>
              <Button data-testid="create-lead-form-submit" onClick={handleCreate} className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
                Create Form
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Forms list */}
        <div className="space-y-3">
          {forms.map(f => (
            <button key={f.form_id} onClick={() => setSelectedForm(f)}
              className={`w-full text-left p-4 rounded-lg border transition-colors duration-200 ${
                selectedForm?.form_id === f.form_id
                  ? 'bg-[#121212] border-[#00FF99]'
                  : 'bg-[#121212] border-[rgba(255,255,255,0.12)] hover:border-[rgba(255,255,255,0.25)]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium text-sm">{f.name}</h3>
                  <p className="text-xs text-[#808080] mt-0.5">{getBizName(f.business_id)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#B0B0B0] bg-[#1A1A1A] px-2 py-0.5 rounded">
                    {f.submissions || 0} leads
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(f.form_id); }}
                    className="p-1 text-[#808080] hover:text-[#FF3B30]">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </button>
          ))}
          {forms.length === 0 && (
            <div className="text-center py-8 text-[#808080]">
              <FormInput size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No forms yet</p>
            </div>
          )}
        </div>

        {/* Form detail / embed codes */}
        <div className="lg:col-span-2">
          {selectedForm ? (
            <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5">
              <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                {selectedForm.name}
              </h2>
              <Tabs defaultValue="embed" className="w-full">
                <TabsList className="bg-[#0A0A0A] border border-[rgba(255,255,255,0.12)]">
                  <TabsTrigger value="embed" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-[#00FF99] text-[#B0B0B0]">
                    <Code size={14} className="mr-1" /> Embed Code
                  </TabsTrigger>
                  <TabsTrigger value="api" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-[#00FF99] text-[#B0B0B0]">
                    <ExternalLink size={14} className="mr-1" /> API
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="embed" className="mt-4">
                  <div className="relative">
                    <pre className="bg-[#0A0A0A] border border-[rgba(255,255,255,0.12)] rounded-md p-4 text-xs text-[#B0B0B0] overflow-x-auto whitespace-pre-wrap font-mono max-h-96">
                      {generateEmbedCode(selectedForm)}
                    </pre>
                    <Button size="sm" onClick={() => copyToClipboard(generateEmbedCode(selectedForm))}
                      className="absolute top-2 right-2 bg-[#1A1A1A] text-white hover:bg-[#00FF99] hover:text-[#0A0A0A] text-xs">
                      <Copy size={12} className="mr-1" /> Copy
                    </Button>
                  </div>
                  <p className="text-xs text-[#808080] mt-2">Paste this code into your website&apos;s HTML to display the lead capture form.</p>
                </TabsContent>
                <TabsContent value="api" className="mt-4">
                  <div className="relative">
                    <pre className="bg-[#0A0A0A] border border-[rgba(255,255,255,0.12)] rounded-md p-4 text-xs text-[#B0B0B0] overflow-x-auto whitespace-pre-wrap font-mono max-h-96">
                      {generateApiDoc(selectedForm)}
                    </pre>
                    <Button size="sm" onClick={() => copyToClipboard(generateApiDoc(selectedForm))}
                      className="absolute top-2 right-2 bg-[#1A1A1A] text-white hover:bg-[#00FF99] hover:text-[#0A0A0A] text-xs">
                      <Copy size={12} className="mr-1" /> Copy
                    </Button>
                  </div>
                  <p className="text-xs text-[#808080] mt-2">Use this endpoint to submit leads from your custom forms or applications.</p>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-12 text-center text-[#808080]">
              <Code size={32} className="mx-auto mb-3 opacity-50" />
              <p>Select a form to view embed codes and API documentation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
